import { useEffect, useRef } from 'react'
import { useSignals } from '@preact/signals-react/runtime'
import type { MoveHistoryEntry } from '../../../../deps/game/moveHistory/moveHistory.types'
import type { MoveHistoryUI } from '../../../../deps/ui/debugPanelVM/moveHistoryUI/moveHistoryUI.types'
import { MoveSummary } from '../MoveSummary/MoveSummary'
import { MoveDetails } from '../MoveDetails/MoveDetails'
import { MoveProgress } from '../MoveProgress/MoveProgress'
import { moveHistoryTestIds } from './moveHistory.testIds'

interface MoveHistoryProps {
  moveHistory: MoveHistoryEntry[]
  ourMateID?: string
  mates: Array<{ id: string; isAI?: boolean }>
  encodeMove?: (move: any) => string
  moveJson: string
  onMoveJsonChange: (json: string) => void
  onSubmitMove: () => void
  aiProgressMessages?: string[]
  moveHistoryUI: MoveHistoryUI
  isLocalMode?: boolean
  isOmniMode?: boolean
}

export function MoveHistory({
  moveHistory,
  ourMateID,
  mates,
  encodeMove,
  moveJson,
  onMoveJsonChange,
  onSubmitMove,
  aiProgressMessages = [],
  moveHistoryUI,
  isLocalMode = false,
  isOmniMode = false
}: MoveHistoryProps) {
  useSignals()
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Autoscroll to bottom when new moves are added
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
    }
  }, [moveHistory.length])


  const formatMove = (entry: MoveHistoryEntry): string => {
    // Handle forceState type specially
    if (typeof entry.move === 'object' && entry.move !== null && 'type' in entry.move && entry.move.type === 'forceState') {
      const stateName = 'stateName' in entry.move ? entry.move.stateName : 'custom'
      return `Forced state: ${stateName}`
    }
    
    // If move is already a string (encoded), return it as-is
    if (typeof entry.move === 'string') {
      return entry.move
    }
    
    // Otherwise, try to encode it if encoder is available
    if (encodeMove) {
      try {
        return encodeMove(entry.move)
      } catch {
        return JSON.stringify(entry.move)
      }
    }
    
    // Fallback to JSON stringify
    return JSON.stringify(entry.move)
  }

  const handleSubmit = () => {
    if (moveJson.trim()) {
      onSubmitMove()
      onMoveJsonChange('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <div className="flex flex-col h-full" data-testid={moveHistoryTestIds.moveHistoryContainer}>
      {/* Move history list - earlier moves at top */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto space-y-1 mb-2"
      >
        {moveHistory.length === 0 ? (
          <div className="text-sm text-muted p-2 text-center">
            No moves yet
          </div>
        ) : (
          <>
            {moveHistory.map((entry) => {
              const isExpanded = moveHistoryUI.expandedMoves.value.has(entry.id)
              const moveDisplay = formatMove(entry)
              const mate = mates.find(m => m.id === entry.mateID)
              const isOurMove = entry.mateID === ourMateID
              const isAI = mate?.isAI || false

              return (
                <div key={entry.id} data-testid={moveHistoryTestIds.getMoveEntryTestId(entry.id)}>
                  <MoveSummary
                    entry={entry}
                    isOurMove={isOurMove}
                    isAI={isAI}
                    moveDisplay={moveDisplay}
                    onClick={() => moveHistoryUI.toggleMove(entry.id)}
                    isLocalMode={isLocalMode}
                    isOmniMode={isOmniMode}
                  />
                  {isExpanded && (
                    <MoveDetails 
                      entry={entry} 
                      aiProgressMessages={[]} 
                    />
                  )}
                </div>
              )
            })}
          </>
        )}
      </div>

      {/* Show AI progress messages for current processing */}
      {aiProgressMessages.length > 0 && (
        <div className="mb-2">
          <MoveProgress messages={aiProgressMessages} />
        </div>
      )}

      {/* Manual move entry */}
      <div className="flex items-center gap-2 border-t pt-2">
        <input
          type="text"
          value={moveJson}
          onChange={(e) => onMoveJsonChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="X on a1"
          className="flex-1 px-2 py-1 text-xs border rounded bg-background text-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <button
          onClick={handleSubmit}
          className="px-2 py-1 rounded transition-colors"
          style={{
            backgroundColor: 'var(--app-color-main)',
            color: 'white',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--app-color-accent)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--app-color-main)'
          }}
          title="Submit move"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-4"
          >
            <path d="M22 2L11 13" />
            <path d="M22 2l-7 20-4-9-9-4 20-7z" />
          </svg>
        </button>
      </div>
    </div>
  )
}
